import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import {
  ChevronDown, ArrowRight, Mail, Globe,
  Compass, Mountain as AlpineIcon, X,
} from 'lucide-react';
import { GET_TOUR_DESTINATIONS, GET_TOUR_CATEGORIES, GET_ALL_TOURS } from '../graphql/tours';

const GOLD = '#B8975A';




/* ─── Coming-soon regions (shown when not yet in DB) ─── */
const COMING_SOON_REGIONS = [
  { slug: 'europe',   label: 'Europe'       },
  { slug: 'asia',     label: 'Asia Pacific' },
  { slug: 'americas', label: 'The Americas' },
];

/* ─── Fonts & Global Styles ─── */
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Jost:wght@300;400;500;600&display=swap');

    .font-cormorant { font-family: 'Cormorant Garamond', serif; }
    .font-jost      { font-family: 'Jost', sans-serif; }

    @keyframes fadeSlideIn {
      from { opacity: 0; transform: translateY(5px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes imgReveal {
      from { opacity: 0; transform: scale(1.04); }
      to   { opacity: 1; transform: scale(1); }
    }
    @keyframes drawerIn  { from { transform: translateX(100%); } to { transform: translateX(0); } }
    @keyframes drawerOut { from { transform: translateX(0); }   to { transform: translateX(100%); } }

    .anim-fade-slide { animation: fadeSlideIn 0.45s cubic-bezier(0.22,1,0.36,1) both; }
    .anim-img-reveal { animation: imgReveal   0.6s  cubic-bezier(0.22,1,0.36,1) both; }
    .drawer-enter    { animation: drawerIn    0.5s  cubic-bezier(0.22,1,0.36,1) both; }
    .drawer-exit     { animation: drawerOut   0.4s  cubic-bezier(0.22,1,0.36,1) both; }

    /* Mega-menu columns: scroll internally instead of clipping long lists */
    .mega-col-scroll { scrollbar-width: thin; scrollbar-color: rgba(184,151,90,0.35) transparent; }
    .mega-col-scroll::-webkit-scrollbar { width: 4px; }
    .mega-col-scroll::-webkit-scrollbar-track { background: transparent; }
    .mega-col-scroll::-webkit-scrollbar-thumb { background: rgba(184,151,90,0.35); border-radius: 2px; }
    .mega-col-scroll::-webkit-scrollbar-thumb:hover { background: rgba(184,151,90,0.6); }

    .nav-link {
      position: relative; display: inline-flex; align-items: center; gap: 6px;
      padding: 8px 16px; font-family: 'Jost', sans-serif; font-size:15px;
      font-weight: 500; letter-spacing:0.14em; text-transform: uppercase;
      text-decoration: none; background: transparent; border: none;
      cursor: pointer; transition: color 0.28s; white-space: nowrap;
    }
    .nav-link::after {
      content: ''; position: absolute; bottom: 0; left: 16px;
      height: 1px; width: 0; background: ${GOLD};
      transition: width 0.35s cubic-bezier(0.22,1,0.36,1);
    }
    .nav-link:hover::after, .nav-link.active::after { width: calc(100% - 32px); }

    .nav-link-t { color: #fff; text-shadow: 0 1px 10px rgba(0,0,0,0.55), 0 0 30px rgba(0,0,0,0.2); }
    .nav-link-t:hover { color: ${GOLD}; text-shadow: none; }
    .nav-link-t.active { color: ${GOLD}; text-shadow: none; }
    .nav-link-s { color: #334155; }
    .nav-link-s:hover { color: ${GOLD}; }
    .nav-link-s.active { color: ${GOLD}; }

    .nav-cta {
      position: relative; display: inline-flex; align-items: center; gap: 10px;
      font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.14em; text-transform: uppercase; padding: 12px 24px;
      border: 1px solid; overflow: hidden; text-decoration: none;
      transition: color 0.35s, border-color 0.35s; white-space: nowrap;
    }
    .nav-cta::before {
      content: ''; position: absolute; inset: 0;
      transform: scaleX(0); transform-origin: left;
      transition: transform 0.38s cubic-bezier(0.22,1,0.36,1); z-index: 0;
    }
    .nav-cta:hover::before { transform: scaleX(1); }
    .nav-cta > * { position: relative; z-index: 1; }
    .nav-cta-t { color: #fff; border-color: rgba(184,151,90,0.7); text-shadow: 0 1px 6px rgba(0,0,0,0.35); }
    .nav-cta-t::before { background: rgba(184,151,90,0.15); }
    .nav-cta-t:hover { color: #fff; border-color: ${GOLD}; text-shadow: none; }
    .nav-cta-s { color: #0f172a; border-color: ${GOLD}; background: ${GOLD}; }
    .nav-cta-s::before { background: #080e1a; }
    .nav-cta-s:hover { border-color: #080e1a; color: #fff; }

    .mob-acc { overflow: hidden; transition: max-height 0.45s cubic-bezier(0.22,1,0.36,1), opacity 0.3s; }
    .mob-acc.open   { max-height: 900px; opacity: 1; }
    .mob-acc.closed { max-height: 0;     opacity: 0; }

    body.nav-locked { overflow: hidden; }
  `}</style>
);

/* ════════════════════════════════════════
   MOBILE DRAWER
════════════════════════════════════════ */
const MobileDrawer = ({ isOpen, onClose, destinations, experienceCategories }) => {
  const [openSection,  setOpenSection]  = useState(null);
  const [openRegion,   setOpenRegion]   = useState(null);
  const [openCountry,  setOpenCountry]  = useState(null);
  const [openCategory, setOpenCategory] = useState(null);
  const [exiting,      setExiting]      = useState(false);
  const location = useLocation();

  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (isOpen) onClose();
    }
  });

  useEffect(() => {
    if (isOpen) document.body.classList.add('nav-locked');
    else        document.body.classList.remove('nav-locked');
    return () => document.body.classList.remove('nav-locked');
  }, [isOpen]);

  const handleClose = () => {
    setExiting(true);
    setTimeout(() => { setExiting(false); onClose(); }, 380);
  };

  const tog = (setter, current, val) => setter(current === val ? null : val);
  if (!isOpen && !exiting) return null;
  const isActive = (path) => location.pathname === path;

  return (
    <>
      <div
        onClick={handleClose}
        className={`fixed inset-0 z-[10001] transition-opacity duration-500 ${isOpen && !exiting ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: 'rgba(8,14,26,0.6)', backdropFilter: 'blur(4px)' }}
      />
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-[380px] z-[10002] flex flex-col ${exiting ? 'drawer-exit' : 'drawer-enter'}`}
        style={{ background: '#FAFAF8' }}
      >
        <div style={{ height: '3px', background: `linear-gradient(90deg, ${GOLD} 55%, rgba(184,151,90,0.12))`, flexShrink: 0 }} />

        <div className="flex items-center justify-between bg-white border-b border-slate-100" style={{ padding: '16px 28px', flexShrink: 0 }}>
          <Link to="/" onClick={handleClose} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '11px' }}>
              <img className='w-20' src="/aio.svg" alt="aio logo" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span className="font-cormorant" style={{ fontSize: '25px', fontWeight: 300, letterSpacing: '.15em', color: '#0f172a', lineHeight: 1 }}>VOLE LUXE</span>
              <span className="font-jost" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD }}>Travel & Safaris</span>
            </div>
          </Link>
          <button
            onClick={handleClose}
            style={{ width: '36px', height: '36px', border: '1px solid #e2d9cd', background: 'transparent', cursor: 'pointer', color: '#12100e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2d9cd'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ padding: '24px 28px 0' }}>
          {[{ label: 'Home', path: '/' }, { label: 'Our Story', path: '/about' }].map(({ label, path }) => (
            <Link key={label} to={path} className="block font-cormorant"
              style={{ fontSize: '31px', fontStyle: 'normal', fontWeight: 300, color: isActive(path) ? GOLD : '#1e293b', padding: '5px 0', textDecoration: 'none', display: 'block', transition: 'color .2s' }}
              onMouseEnter={e => e.currentTarget.style.color = GOLD}
              onMouseLeave={e => e.currentTarget.style.color = isActive(path) ? GOLD : '#1e293b'}
            >
              {label}
              {isActive(path) && <span style={{ display: 'block', width: '20px', height: '1px', background: GOLD, marginTop: '4px' }} />}
            </Link>
          ))}

          <div style={{ padding: '16px 0 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '28px', height: '1px', background: GOLD }} />
            <span className="font-jost" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,151,90,.65)' }}>Explore</span>
          </div>

          {/* Destinations — from DB */}
          <div>
            <button onClick={() => tog(setOpenSection, openSection, 'dest')} className="w-full flex items-center justify-between"
              style={{ padding: '5px 0', background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <span className="font-cormorant" style={{ fontSize: '31px', fontStyle: 'normal', fontWeight: 300, color: location.pathname.startsWith('/tours') ? GOLD : '#1e293b' }}>Destinations</span>
              <ChevronDown size={15} strokeWidth={1.5} style={{ color: GOLD, transition: 'transform .3s', transform: openSection === 'dest' ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
            </button>
            <div className={`mob-acc ${openSection === 'dest' ? 'open' : 'closed'}`}>
              <div style={{ paddingLeft: '16px', paddingTop: '4px', paddingBottom: '8px' }}>
                {destinations.map((region) => (
                  <div key={region.name}>
                    <button onClick={() => !region.comingSoon && tog(setOpenRegion, openRegion, region.name)}
                      className="w-full flex items-center justify-between"
                      style={{ padding: '8px 0', background: 'transparent', border: 'none', cursor: region.comingSoon ? 'default' : 'pointer' }}>
                      <span className="font-jost" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: openRegion === region.name ? GOLD : '#64748b', transition: 'color .2s' }}>{region.name}</span>
                      {region.comingSoon
                        ? <span className="font-jost" style={{ fontSize: '12px', color: '#6b7280', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Soon</span>
                        : region.countries?.length > 0 && <ChevronDown size={11} strokeWidth={1.5} style={{ color: '#12100e', transition: 'transform .3s', transform: openRegion === region.name ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
                      }
                    </button>
                    {!region.comingSoon && (
                      <div className={`mob-acc ${openRegion === region.name ? 'open' : 'closed'}`}>
                        <div style={{ paddingLeft: '16px', paddingBottom: '8px' }}>
                          {region.countries.map((country) => (
                            <div key={country.name}>
                              <button onClick={() => tog(setOpenCountry, openCountry, country.name)}
                                className="w-full flex items-center justify-between"
                                style={{ padding: '6px 0', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                                <span className="font-cormorant" style={{ fontSize: '23px', fontStyle: 'normal', fontWeight: 300, color: '#12100e' }}>{country.name}</span>
                                {country.parks?.length > 0 && <ChevronDown size={11} strokeWidth={1.5} style={{ color: '#12100e', transition: 'transform .3s', transform: openCountry === country.name ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />}
                              </button>
                              <div className={`mob-acc ${openCountry === country.name ? 'open' : 'closed'}`}>
                                <div style={{ paddingLeft: '16px', paddingBottom: '8px' }}>
                                  {country.parks?.map((park) => (
                                    <Link key={park.name} to={park.path}
                                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', textDecoration: 'none', borderBottom: '1px solid #f0ece6' }}>
                                      <span className="font-jost" style={{ fontSize: '13px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#12100e' }}>{park.name}</span>
                                      <ArrowRight size={11} style={{ color: GOLD, flexShrink: 0 }} />
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Experiences — still static */}
          <div>
            <button onClick={() => tog(setOpenSection, openSection, 'exp')} className="w-full flex items-center justify-between"
              style={{ padding: '5px 0', background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <span className="font-cormorant" style={{ fontSize: '31px', fontStyle: 'normal', fontWeight: 300, color: '#1e293b' }}>Experiences</span>
              <ChevronDown size={15} strokeWidth={1.5} style={{ color: GOLD, transition: 'transform .3s', transform: openSection === 'exp' ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
            </button>
            <div className={`mob-acc ${openSection === 'exp' ? 'open' : 'closed'}`}>
              <div style={{ paddingLeft: '16px', paddingTop: '4px', paddingBottom: '8px' }}>
                {experienceCategories.map((cat) => (
                  <div key={cat.name}>
                    <button onClick={() => !cat.comingSoon && tog(setOpenCategory, openCategory, cat.name)}
                      className="w-full flex items-center justify-between"
                      style={{ padding: '8px 0', background: 'transparent', border: 'none', cursor: cat.comingSoon ? 'default' : 'pointer' }}>
                      <span className="font-jost" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: openCategory === cat.name ? GOLD : '#64748b', transition: 'color .2s' }}>{cat.name}</span>
                      {cat.comingSoon
                        ? <span className="font-jost" style={{ fontSize: '12px', color: '#6b7280', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Soon</span>
                        : cat.activities?.length > 0 && <ChevronDown size={11} strokeWidth={1.5} style={{ color: '#12100e', transition: 'transform .3s', transform: openCategory === cat.name ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
                      }
                    </button>
                    {!cat.comingSoon && (
                      <div className={`mob-acc ${openCategory === cat.name ? 'open' : 'closed'}`}>
                        <div style={{ paddingLeft: '16px', paddingBottom: '8px' }}>
                          {cat.activities?.map((act) => (
                            <Link key={act.name} to={act.path}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', textDecoration: 'none', borderBottom: '1px solid #f0ece6' }}>
                              <span className="font-cormorant" style={{ fontSize: '23px', fontStyle: 'normal', fontWeight: 300, color: '#12100e' }}>{act.name}</span>
                              <ArrowRight size={11} style={{ color: GOLD, flexShrink: 0 }} />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ height: '24px' }} />
        </div>

        <div style={{ padding: '18px 28px', borderTop: '1px solid #f0ece6', background: '#fff', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Link to="/contact"
            style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: isActive('/contact') ? GOLD : '#475569', textDecoration: 'none', transition: 'color .2s' }}
            onMouseEnter={e => e.currentTarget.style.color = GOLD}
            onMouseLeave={e => e.currentTarget.style.color = isActive('/contact') ? GOLD : '#475569'}
          >
            <Mail size={13} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
            Enquire About a Journey
          </Link>
          <Link to="/tours"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '15px', background: GOLD, color: '#080e1a', textDecoration: 'none' }}>
            Plan Your Safari <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </>
  );
};

/* ════════════════════════════════════════
   MAIN NAVBAR
════════════════════════════════════════ */
const Navbar = () => {
  const [scrolled,       setScrolled]       = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpen,     setMobileOpen]     = useState(false);
  const location = useLocation();

  const [destSelection, setDestSelection] = useState({ region: null, country: null, park: null });
  const [expSelection,  setExpSelection]  = useState({ category: null, activity: null });

  const timeoutRef = useRef(null);

  const isActive    = (path)   => location.pathname === path;
  const isActiveSub = (prefix) => location.pathname.startsWith(prefix);

  /* ── Fetch destinations from DB ── */
  const { data: destData } = useQuery(GET_TOUR_DESTINATIONS, { fetchPolicy: 'cache-first' });
  const { data: catData  } = useQuery(GET_TOUR_CATEGORIES,   { fetchPolicy: 'cache-first' });
  const { data: toursData } = useQuery(GET_ALL_TOURS, {
    variables: { limit: 200, offset: 0 },
    fetchPolicy: 'cache-first',
  });

  /* ── Build destinations tree from DB data ── */
  const destinations = useMemo(() => {
    if (!destData?.tourCountries?.length) return [];
  
    const regionMap = {};
    destData.tourCountries.forEach(country => {
      const slug  = country.frontendContinentSlug || 'africa';
      // Use continentName from DB instead of the hardcoded map
      const label = country.continentName || slug.charAt(0).toUpperCase() + slug.slice(1);
  
      if (!regionMap[slug]) {
        regionMap[slug] = { name: label, path: `/tours?loc=${slug}`, countries: [] };
      }
      regionMap[slug].countries.push({
        name: country.name,
        path: `/tours?country=${country.slug}`,
        parks: (country.parks || []).map(park => ({
          name: park.name,
          path: `/tours?search=${park.slug}`,
          img:  park.heroImage || park.featuredImage || '',  // DB first
          desc: park.shortDescription || `Explore ${park.name} — one of the finest safari destinations in ${country.name}.`,
        })),
      });
    });
  
    const activeSlugs = Object.keys(regionMap);
    const comingSoon  = COMING_SOON_REGIONS
      .filter(r => !activeSlugs.includes(r.slug))
      .map(r => ({ name: r.label, path: `/tours?loc=${r.slug}`, countries: [], comingSoon: true }));
  
    return [...Object.values(regionMap), ...comingSoon];
  }, [destData]);

  /* ── Experience categories, each with its real tours as "activities" ── */
 const experienceCategories = useMemo(() => {
  if (!catData?.tourCategories?.length) return [];

  const toursByCategory = {};
  (toursData?.tours || []).forEach(tour => {
    const slug = tour.category?.slug;
    if (!slug) return;
    if (!toursByCategory[slug]) toursByCategory[slug] = [];
    toursByCategory[slug].push({
      name: tour.name,
      path: `/tours/${tour.slug}`,
      img:  tour.featuredImage || '',
      desc: tour.tagline || '',
    });
  });

  return catData.tourCategories.map(cat => ({
    name:       cat.name,
    path:       `/tours?cat=${cat.slug}`,
    activities: toursByCategory[cat.slug] || [],
    img:        cat.heroImage || '',
    desc:       cat.description || '',
  }));
}, [catData, toursData]);

  /* ── Handlers ── */
  const handleMouseEnter = (menu) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(menu);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
      setDestSelection({ region: null, country: null, park: null });
      setExpSelection({ category: null, activity: null });
    }, 180);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setActiveDropdown(null); setMobileOpen(false); }, [location.pathname]);

  const T            = !scrolled;
  const dropdownActive = isActiveSub('/tours');

  const linkClass = (path, extra = '') => {
    const active = path ? isActive(path) : false;
    return `nav-link ${T ? 'nav-link-t' : 'nav-link-s'} ${active ? 'active' : ''} ${extra}`.trim();
  };

  const primaryItems    = activeDropdown === 'dest' ? destinations : experienceCategories;
  const activeRegion    = activeDropdown === 'dest' ? destSelection.region    : expSelection.category;
  const activeSecondary = activeDropdown === 'dest' ? destSelection.region?.countries : expSelection.category?.activities;
  const activePreview   = activeDropdown === 'dest' ? destSelection.park      : expSelection.activity;

  const comingSoonBlock = (
    <div style={{ padding: '16px 4px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ width: '24px', height: '1px', background: GOLD }} />
      <p className="font-cormorant" style={{ fontSize: '27px', fontStyle: 'normal', fontWeight: 300, color: '#12100e', lineHeight: 1.4 }}>
        New safaris<br />coming soon.
      </p>
      <p className="font-jost" style={{ fontSize: '13px', color: '#12100e', letterSpacing: '0.16em', textTransform: 'uppercase' }}>
        Notify me when available →
      </p>
    </div>
  );

  return (
    <>
      <FontLoader />

      <MobileDrawer
        isOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        destinations={destinations}
        experienceCategories={experienceCategories}
      />

      <nav
        className="fixed top-0 left-0 right-0 z-[10000] font-jost"
        style={{
          transition: 'background .6s cubic-bezier(0.22,1,0.36,1), box-shadow .6s',
          background: scrolled
            ? 'rgba(249,247,243,0.97)'
            : 'linear-gradient(to bottom, rgba(8,14,26,0.68) 0%, rgba(8,14,26,0.28) 60%, transparent 100%)',
          backdropFilter: scrolled ? 'blur(20px) saturate(160%)' : 'none',
          boxShadow: scrolled ? '0 2px 32px rgba(0,0,0,0.06)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(184,151,90,0.15)' : 'none',
        }}
      >
        <div style={{ height: '2px', background: GOLD, opacity: scrolled ? 1 : 0, transition: 'opacity .5s' }} />

        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-10 flex justify-between items-center relative">

          {/* ── LOGO ── */}
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '13px', padding: '16px 0' }}>
            <img className='w-16 h-16' src="/aio.svg" alt="aio logo" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', lineHeight: 1 }}>
              <span className="font-cormorant" style={{ fontSize: 'clamp(21px,1.6vw,21px)', fontWeight: 300, letterSpacing: '0.14em', transition: 'color .4s, text-shadow .4s', color: scrolled ? '#0f172a' : '#fff', textShadow: T ? '0 1px 10px rgba(0,0,0,0.5), 0 0 30px rgba(0,0,0,0.15)' : 'none' }}>
                VOLE LUXE
              </span>
              <span className="font-jost" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD }}>
                Travel & Safaris
              </span>
            </div>
          </Link>

          {/* ── DESKTOP NAV ── */}
          <div className="hidden lg:flex items-center">
            <Link to="/" className={linkClass('/')} style={{ padding: '22px 16px' }}>Home</Link>
            {['dest', 'exp'].map((key) => (
              <div key={key} onMouseEnter={() => handleMouseEnter(key)} onMouseLeave={handleMouseLeave}>
                <button
                  className={`nav-link ${T ? 'nav-link-t' : 'nav-link-s'} ${dropdownActive ? 'active' : ''} ${activeDropdown === key ? 'active' : ''}`}
                  style={{ padding: '22px 16px' }}
                >
                  {key === 'dest' ? 'Destinations' : 'Experiences'}
                  <ChevronDown size={10} strokeWidth={2} style={{ transition: 'transform .3s', transform: activeDropdown === key ? 'rotate(180deg)' : 'none' }} />
                </button>
              </div>
            ))}
            <Link to="/about" className={linkClass('/about')} style={{ padding: '22px 16px' }}>Our Story</Link>
          </div>

          {/* ── DESKTOP ACTIONS ── */}
          <div className="hidden lg:flex items-center" style={{ gap: '28px', padding: '20px 0' }}>
            <Link to="/contact" className="flex items-center font-jost"
              style={{ gap: '7px', fontSize: '13px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', textDecoration: 'none', transition: 'color .28s', color: isActive('/contact') ? GOLD : (T ? '#fff' : '#4b5563'), textShadow: T && !isActive('/contact') ? '0 1px 8px rgba(0,0,0,0.45)' : 'none' }}
              onMouseEnter={e => { e.currentTarget.style.color = GOLD; e.currentTarget.style.textShadow = 'none'; }}
              onMouseLeave={e => { e.currentTarget.style.color = isActive('/contact') ? GOLD : (T ? '#fff' : '#4b5563'); e.currentTarget.style.textShadow = T && !isActive('/contact') ? '0 1px 8px rgba(0,0,0,0.45)' : 'none'; }}
            >
              <Mail size={13} strokeWidth={1.5} /> Enquire
            </Link>
            <span style={{ color: GOLD, fontSize: '12px', opacity: 0.5, userSelect: 'none' }}>◆</span>
            <Link to="/tours" className={`nav-cta ${T ? 'nav-cta-t' : 'nav-cta-s'}`}>
              <span>Plan Your Safari</span>
              <ArrowRight size={11} strokeWidth={2} />
            </Link>
          </div>

          {/* ── MOBILE RIGHT ── */}
          <div className="flex lg:hidden items-center" style={{ gap: '16px' }}>
            <Link to="/contact" className="hidden sm:flex items-center font-jost"
              style={{ gap: '6px', fontSize: '13px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', textDecoration: 'none', transition: 'color .28s', color: isActive('/contact') ? GOLD : (T ? '#fff' : '#4b5563'), textShadow: T ? '0 1px 8px rgba(0,0,0,0.45)' : 'none' }}
              onMouseEnter={e => e.currentTarget.style.color = GOLD}
              onMouseLeave={e => e.currentTarget.style.color = isActive('/contact') ? GOLD : (T ? '#fff' : '#4b5563')}
            >
              <Mail size={12} strokeWidth={1.5} /> Enquire
            </Link>
            <button onClick={() => setMobileOpen(true)} aria-label="Open navigation"
              style={{ width: '40px', height: '40px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: '6px', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <span style={{ display: 'block', height: '1px', width: '24px', transition: 'background .3s', background: scrolled ? '#334155' : '#fff', boxShadow: T ? '0 0 6px rgba(0,0,0,0.6)' : 'none' }} />
              <span style={{ display: 'block', height: '1px', width: '16px', transition: 'background .3s', background: scrolled ? '#334155' : '#fff', boxShadow: T ? '0 0 6px rgba(0,0,0,0.6)' : 'none' }} />
              <span style={{ display: 'block', height: '1px', width: '24px', background: GOLD }} />
            </button>
          </div>

          {/* ── DESKTOP MEGA MENU ── */}
          <div
            onMouseEnter={() => handleMouseEnter(activeDropdown)}
            onMouseLeave={handleMouseLeave}
            className={`absolute top-full left-1/2 w-full bg-white overflow-hidden z-[10001] ${activeDropdown ? 'pointer-events-auto' : 'pointer-events-none'}`}
            style={{
              borderTop: `2px solid ${GOLD}`,
              borderLeft: 'none', borderRight: 'none', borderBottom: 'none',
              borderRadius: '0 0 12px 12px',
              boxShadow: '0 40px 90px -20px rgba(8,14,26,0.28), 0 4px 24px rgba(8,14,26,0.06)',
              transition: 'max-height .48s cubic-bezier(0.22,1,0.36,1), opacity .35s ease, transform .45s cubic-bezier(0.22,1,0.36,1)',
              maxHeight: activeDropdown ? '480px' : '0',
              opacity:   activeDropdown ? 1 : 0,
              transform: `translateX(-50%) translateY(${activeDropdown ? '0' : '-8px'})`,
            }}
          >
            <div className="flex" style={{ height: 'min(440px, calc(100vh - 160px))' }}>

              {/* Col 1 — Regions / Categories */}
              <div style={{ width: '22%', padding: '36px 32px', background: '#FAFAF8', borderRight: '1px solid #f0ece6', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flexShrink: 0, minWidth: 0 }}>
                <div className="mega-col-scroll" style={{ overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                  <span className="font-jost" style={{ fontSize: '12px', fontWeight: 600, color: '#12100e', letterSpacing: '0.2em', textTransform: 'uppercase', display: 'block', marginBottom: '28px' }}>
                    {activeDropdown === 'dest' ? 'Region' : 'Category'}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {primaryItems.map((item, i) => (
                      <Link key={item.name} to={item.path} className="font-cormorant anim-fade-slide"
                        onMouseEnter={() => activeDropdown === 'dest'
                          ? setDestSelection({ region: item, country: null, park: null })
                          : setExpSelection({ category: item, activity: null })
                        }
                        style={{ fontSize: '25px', fontStyle: 'normal', fontWeight: 300, textDecoration: 'none', display: 'block', transition: 'all .28s', color: activeRegion?.name === item.name ? GOLD : '#94a3b8', transform: activeRegion?.name === item.name ? 'translateX(4px)' : 'none', animationDelay: `${i * 40}ms` }}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '24px', flexShrink: 0 }}>
                  <div style={{ width: '18px', height: '1px', background: GOLD }} />
                  <span className="font-jost" style={{ fontSize: '12px', letterSpacing: '0.16em', color: '#12100e', textTransform: 'uppercase' }}>Since 1998</span>
                </div>
              </div>

              {/* Col 2 — Countries / Experiences */}
              <div className="mega-col-scroll" style={{ width: '20%', padding: '36px 28px', background: '#fff', borderRight: '1px solid #f0ece6', flexShrink: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                <span className="font-jost" style={{ fontSize: '12px', fontWeight: 600, color: '#12100e', letterSpacing: '0.2em', textTransform: 'uppercase', display: 'block', marginBottom: '28px' }}>
                  {activeDropdown === 'dest' ? 'Country' : 'Experience'}
                </span>
                {activeRegion?.comingSoon ? comingSoonBlock : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {activeSecondary?.map((sub, i) => {
                      const isActiveItem = (activeDropdown === 'dest' ? destSelection.country?.name : expSelection.activity?.name) === sub.name;
                      return (
                        <Link key={sub.name} to={sub.path} className="font-jost anim-fade-slide"
                          onMouseEnter={() => activeDropdown === 'dest'
                            ? setDestSelection(s => ({ ...s, country: sub, park: null }))
                            : setExpSelection(s => ({ ...s, activity: sub }))
                          }
                          style={{ display: 'flex', alignItems: 'center', gap: '0', fontSize: '13px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', textDecoration: 'none', transition: 'color .25s', animationDelay: `${i * 50}ms`, color: isActiveItem ? '#0f172a' : '#64748b' }}
                        >
                          <span style={{ display: 'inline-block', height: '1px', background: GOLD, transition: 'width .3s, margin-right .3s', width: isActiveItem ? '12px' : '0', marginRight: isActiveItem ? '8px' : '0', flexShrink: 0 }} />
                          {sub.name}
                        </Link>
                      );
                    })}
                    {!activeSecondary?.length && activeRegion && comingSoonBlock}
                  </div>
                )}
              </div>

              {/* Col 3 — Parks / Activity detail */}
              <div className="mega-col-scroll" style={{ width: '23%', padding: '36px 28px', background: '#FAFAF8', borderRight: '1px solid #f0ece6', flexShrink: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                <span className="font-jost" style={{ fontSize: '12px', fontWeight: 600, color: '#12100e', letterSpacing: '0.2em', textTransform: 'uppercase', display: 'block', marginBottom: '28px' }}>
                  Safaris
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {activeDropdown === 'dest' && destSelection.country?.parks?.map((p, i) => (
                    <Link key={p.name} to={p.path} className="anim-fade-slide"
                      onMouseEnter={() => setDestSelection(s => ({ ...s, park: p }))}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', textDecoration: 'none', transition: 'all .25s', animationDelay: `${i * 60}ms`, background: destSelection.park?.name === p.name ? '#fff' : 'transparent', boxShadow: destSelection.park?.name === p.name ? '0 2px 10px rgba(0,0,0,0.05)' : 'none' }}
                    >
                      <span className="font-cormorant" style={{ fontSize: '21px', fontStyle: 'normal', fontWeight: 300, transition: 'color .25s', color: destSelection.park?.name === p.name ? '#0f172a' : '#64748b' }}>
                        {p.name}
                      </span>
                      <ArrowRight size={13} style={{ color: GOLD, flexShrink: 0, transition: 'all .25s', opacity: destSelection.park?.name === p.name ? 1 : 0, transform: destSelection.park?.name === p.name ? 'translateX(0)' : 'translateX(-6px)' }} />
                    </Link>
                  ))}
                  {activeDropdown === 'exp' && expSelection.activity && (
                    <div style={{ padding: '4px 4px' }}>
                      <p className="font-cormorant" style={{ fontSize: '27px', fontStyle: 'normal', fontWeight: 300, color: '#0f172a', lineHeight: 1.2, marginBottom: '10px' }}>{expSelection.activity.name}</p>
                      <p className="font-jost" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.7, marginBottom: '16px' }}>{expSelection.activity.desc}</p>
                      <Link to={expSelection.activity.path} className="font-jost" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD }}>
                        View Itinerary <ArrowRight size={11} />
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Col 4 — Image preview */}
              <div style={{ flex: 1, position: 'relative', background: '#0f172a', overflow: 'hidden' }}>
                {activePreview ? (
                  <div key={activePreview.name} className="anim-img-reveal" style={{ height: '100%' }}>
                    <img src={activePreview.img} alt={activePreview.name} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: .78 }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(8,14,26,.92) 0%, rgba(8,14,26,.18) 55%, transparent 100%)' }} />
                    <div style={{ position: 'absolute', inset: 0, padding: '36px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                      <div style={{ width: '28px', height: '1px', background: GOLD, marginBottom: '16px' }} />
                      <h3 className="font-cormorant" style={{ fontSize: '36px', fontStyle: 'normal', fontWeight: 300, color: '#fff', lineHeight: 1, marginBottom: '10px' }}>{activePreview.name}</h3>
                      <p className="font-jost" style={{ fontSize: '15px', fontWeight: 300, color: 'rgba(255,255,255,.7)', lineHeight: 1.7, maxWidth: '260px', marginBottom: '22px' }}>{activePreview.desc}</p>
                      <Link to={activePreview.path} className="font-jost" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', textDecoration: 'none', fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD }}>
                        View Full Itinerary <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '36px', background: 'linear-gradient(135deg, #080e1a 0%, #1a2744 100%)', position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: 0, opacity: .04, backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <div style={{ width: '32px', height: '1px', background: 'rgba(184,151,90,.35)', marginBottom: '16px' }} />
                      <p className="font-cormorant" style={{ fontSize: '32px', fontStyle: 'normal', fontWeight: 300, color: 'rgba(255,255,255,.22)', lineHeight: 1.4 }}>
                        Select a destination<br />to explore.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Desktop backdrop */}
      <div
        className={`fixed inset-0 z-[9999] transition-opacity duration-500 hidden lg:block ${activeDropdown ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(8,14,26,0.42)' }}
      />
    </>
  );
};

export default Navbar;